import { useQuery, useQueryClient } from 'react-query';
import {
  getHudVoucherStatsQueryKey,
  getRegularAssistanceQueryKey,
} from './hooks.hudVoucherQueryKeys';
import HudVoucherService from '../../services/hudVoucherService';
import { useMemo } from 'react';
import { toastr } from 'react-redux-toastr';
import { buildRegularAssistanceViewModel } from './RegularAssistancePayments/adapter';

const getQueryErrorMessage = (error) => {
  if (error == null) return '';
  if (typeof error === 'string') return error;
  if (typeof error.message === 'string' && error.message) return error.message;
  const networkError = error.networkError;
  if (typeof networkError === 'string') return networkError;
  if (networkError && typeof networkError.message === 'string') {
    return networkError.message;
  }
  if (typeof error.error === 'string') return error.error;
  return '';
};

const useRegularAssistanceQuery = ({
  organizationId,
  propertyId,
  hudVoucherId,
  voucher,
  options: baseOptions,
}) => {
  const queryClient = useQueryClient();
  const options = useMemo(
    () => ({
      ...baseOptions,
      enabled: Boolean(organizationId && propertyId && hudVoucherId),
      refetchOnWindowFocus: false,
      retry: false,
      // The Regular Assistance GET can build or clear rows. A stats request
      // still in flight predates that write, so it is cancelled, not joined.
      onSuccess: async () => {
        const statsQueryKey = getHudVoucherStatsQueryKey(
          organizationId,
          propertyId,
          hudVoucherId,
        );
        await queryClient.cancelQueries(statsQueryKey);
        await queryClient.invalidateQueries(statsQueryKey);
      },
      onError: (e) => {
        const msg =
          getQueryErrorMessage(e) ||
          'Failed to load Regular Assistance rows. Please try again.';
        toastr.error('Error', msg);
      },
    }),
    [baseOptions, hudVoucherId, organizationId, propertyId, queryClient],
  );

  const query = useQuery(
    getRegularAssistanceQueryKey(organizationId, propertyId, hudVoucherId),
    async () => {
      const service = new HudVoucherService();
      return service.getHudVoucherRegularAssistance(
        organizationId,
        propertyId,
        hudVoucherId,
      );
    },
    options,
  );

  const data = useMemo(() => {
    return buildRegularAssistanceViewModel({
      voucher,
      regularAssistanceResponse: query.data,
    });
  }, [query.data, voucher]);

  return {
    ...query,
    data,
  };
};

export default useRegularAssistanceQuery;
